import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import Utilities from 'api/lib/Utilities';
import FacilityUsersProxy from 'api/facilityUsers/facilityUsersProxy';
import WorklistProxy from 'api/CoreMeasures/WorklistProxy';

import PageLayout from 'global_elements/Layouts/PageLayout';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import InlineText from 'global_elements/Text/InlineText';
import SecondaryHeader from 'global_elements/Text/SecondaryHeader';
import ProviderWorkList from 'global_elements/Layouts/DataTables/ProviderWorkList';
import SearchBar from 'global_elements/Inputs/SearchBar';

import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { EmailedAssessmentRow, ProviderWorkListRow, WorkListChartDataType } from 'types/tableProps';
import { ActiveFacilityContext } from 'context/activeFacility';
import { AllRoutes } from 'constants/routes';
import { WorkListChart } from 'pages/shared/WorkCharts/workCharts';
import { MultiStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/MultiSelect/styles';
import { ProgramSelectOption, SelectOption } from 'types/inputProps';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import './styles.scss';
import { UserContext } from 'context/user';
import { AccessType } from 'types/facilityUserAccount';
import Button from 'global_elements/Button';
import { ButtonVariant } from 'global_elements/Button/variants';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import MHODateTime from 'domain/dateTime/MHODateTime';
import DataListsProxy, { DataListType } from 'api/dataLists/dataListsProxy';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import { LabeledDatePickerInput } from 'global_elements/Inputs/LabeledDatePickerInput';
import LabeledMultiSelect from 'global_elements/Inputs/Dropdown/MultiSelect/LabeledMultiSelect';
import Paragraph from 'global_elements/Text/Paragraph';
import { EmailedAssessment, WorkListPatient } from 'interfaces/patients/patientData';
import EmailedAssessmentsTable from 'global_elements/Layouts/DataTables/EmailedAssessmentsTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AssessmentProxy from 'api/assessments/assessmentProxy';

interface FacilityDashboardProps {
  testText: string;
  title: string;
}

/**
 * Shared dashboard page for Providers and Facility Directors.
 */
export const FacilityDashboard = (props: FacilityDashboardProps): React.JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [workListLoading, setWorkListLoading] = useState(true);
  const [emailedAssessmentsLoading, setEmailedAssessmentsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [emailedAssessmentsData, setEmailedAssessmentsData] = useState<EmailedAssessmentRow[]>([]);
  const [filteredEmailedAssessmentsData, setFilteredEmailedAssessmentsData] = useState<EmailedAssessmentRow[]>([]);
  const [worklistSearchText, setWorklistSearchText] = useState<string>('');
  const [emailedAssessmentsSearchText, setEmailedAssessmentsSearchText] = useState<string>('');
  const [workListData, setWorkListData] = useState<ProviderWorkListRow[]>([]);
  const [workListChartData, setWorkListChartData] = useState<WorkListChartDataType>({
    facilityID: 0,
    chartData: [],
  });
  const [filteredList, setFilteredList] = useState<ProviderWorkListRow[]>([]);
  const { facility } = useContext(ActiveFacilityContext);
  const { user } = useContext(UserContext);
  const [isLoadingChart, setIsLoadingChart] = useState<boolean>(false);
  const [showProgramChart, setShowProgramChart] = useState<boolean>(true);
  const history = useHistory();
  const [serviceCodeOptions, setServiceCodeOptions] = useState<SelectOption[]>([]);
  const [selectedServiceCodes, setSelectedServiceCodes] = useState<SelectOption[]>([]);
  const [programCodeOptions, setProgramCodeOptions] = useState<ProgramSelectOption[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<ProgramSelectOption | null>(null);
  const [selectedEmailProgram, setSelectedEmailProgram] = useState<ProgramSelectOption | null>(null);
  const [statusOptions, setStatusOptions] = useState<SelectOption[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(null);
  const [linkStatusOptions, setLinkStatusOptions] = useState<SelectOption[]>([]);
  const [selectedLinkStatus, setSelectedLinkStatus] = useState<SelectOption | null>(null);
  const [awidOptions, setAwidOptions] = useState<SelectOption[]>([]);
  const [selectedAwid, setSelectedAwid] = useState<SelectOption | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const getWorklist = (): void => {
    if (!facility.value.length) {
      return;
    }

    setWorkListLoading(true);
    const unitCodes = selectedServiceCodes.map((o) => o.value);
    const selectedProgramID: number | null = selectedProgram ? parseInt(selectedProgram.value, 10) : null;
    const selectedStatusID: number | null = selectedStatus ? parseInt(selectedStatus.value, 10) : null;
    FacilityUsersProxy.getProviderWorkList(facility.value, undefined, unitCodes, selectedProgramID, startDate, endDate, selectedStatusID)
      .then((resp) => {
        setLoading(false);
        if (resp.data) {
          const filteredWorkList: ProviderWorkListRow[] = resp.data
            .filter((row: WorkListPatient) => row.patientName !== null)
            .map(
              (row: WorkListPatient) => ({
                patientName: row.patientName,
                accountNumber: row.patientNumber,
                patientID: row.patientID,
                medicalRecord: row.medicalRecordNumber,
                admissionDate: row.dateAdmitted,
                program: row.programName,
                unit: row.unitCode ?? '',
                provider: row.providerName ?? '',
                patientStatus: row.lookupDesc,
                registrationPIN: row.registrationPIN ?? '',
              }) as ProviderWorkListRow,
            );
          setWorkListData(filteredWorkList);
          setFilteredList(filteredWorkList);
        } else if (resp.message) {
          setWorkListData([
            {
              patientName: resp.message,
              accountNumber: '',
              patientID: -1,
              medicalRecord: '',
              admissionDate: '',
              program: '',
              unit: '',
              provider: '',
              patientStatus: '',
              registrationPIN: '',
            },
          ]);
        }

        setWorkListLoading(false);
      })
      .catch((err) => {
        setWorkListLoading(false);
        console.log(err);
        setWorkListData([
          {
            patientName: 'An error occurred.',
            accountNumber: '',
            patientID: -1,
            medicalRecord: '',
            admissionDate: '',
            program: '',
            unit: '',
            provider: '',
            patientStatus: '',
            registrationPIN: '',
          },
        ]);
      });
  };

  const getEmailedAssessments = (): void => {
    if (!facility.value.length) {
      return;
    }

    const programID: number | undefined = selectedEmailProgram && selectedEmailProgram.value !== '' ? parseInt(selectedEmailProgram.value, 10) : undefined;
    const linkStatusID: number | undefined = selectedLinkStatus && selectedLinkStatus.value !== '' ? parseInt(selectedLinkStatus.value, 10) : undefined;
    const appliedWhenID: number | undefined = selectedAwid && selectedAwid.value !== '' ? parseInt(selectedAwid.value, 10) : undefined;
    const facilityID = parseInt(facility.value, 10);

    setEmailedAssessmentsLoading(true);
    FacilityUsersProxy.GetEmailedAssessments(facilityID, programID, appliedWhenID, linkStatusID)
      .then((resp) => {
        if (resp.data) {
          const filteredEmailAssessments: EmailedAssessmentRow[] = resp.data
            .filter((row: EmailedAssessment) => row.patientName !== null)
            .map(
              (row: EmailedAssessment) => ({
                patientName: row.patientName,
                accountNumber: row.patientNumber,
                patientID: row.patientID,
                medicalRecord: row.medicalRecordNumber,
                admissionDate: row.dateAdmitted,
                dischargeDate: row.dateDischarged,
                program: row.programName,
                user: row.accountEmail,
                assessmentSentTo: row.assessmentLinkEmail,
                assessment: row.assessmentName,
                assessmentLinkStatus: row.assessmentLinkStatus,
                assessmentTimePeriod: row.appliedWhen,
              }) as EmailedAssessmentRow,
            );
          setEmailedAssessmentsData(filteredEmailAssessments);
          setFilteredEmailedAssessmentsData(filteredEmailAssessments);
        } else if (resp.message) {
          setEmailedAssessmentsData([
            {
              patientName: '',
              accountNumber: '',
              patientID: -1,
              medicalRecord: '',
              admissionDate: '',
              dischargeDate: '',
              program: '',
              user: '',
              assessmentSentTo: '',
              assessment: '',
              assessmentLinkStatus: '',
              assessmentTimePeriod: '',
            },
          ]);
        }

        setEmailedAssessmentsLoading(false);
      })
      .catch((err) => {
        setEmailedAssessmentsLoading(false);
        console.log(err);
        setEmailedAssessmentsData([
          {
            patientName: '',
            accountNumber: '',
            patientID: -1,
            medicalRecord: '',
            admissionDate: '',
            dischargeDate: '',
            program: '',
            user: '',
            assessmentSentTo: '',
            assessment: '',
            assessmentLinkStatus: '',
            assessmentTimePeriod: '',
          },
        ]);
      });
  };

  useEffect(() => {
    if (user?.accessType === AccessType.CORE_MEASURES) {
      history.push('/core-measures-worklist');
    }

    DataListsProxy.getOptionListV2(DataListType.WorklistAlert)
      .then((response) => {
        if (response.data) {
          const statusList: SelectOption[] = response.data.map((x) => ({
            label: x.lookupDesc,
            value: x.lookupID.toString(),
          }));
          setStatusOptions(statusList);
        }
      })
      .catch((error) => {
        console.log(error);
      });

    DataListsProxy.getOptionListV2(DataListType.AssessmentLinkStatus).then((response) => {
      if (response.data) {
        const myOptions: SelectOption[] = response.data.map((option) => ({
          label: option.lookupDesc,
          value: option.lookupID.toString(),
        }));
        setLinkStatusOptions(myOptions);
      }
    }).catch((error) => {
      console.log(error);
    });

    AssessmentProxy.getAppliedWhens(0).then((data) => {
      if (data.length > 0) {
        const myOptions: SelectOption[] = data.map((option) => ({
          label: option.appliedWhen,
          value: option.appliedWhenID.toString(),
        }));
        setAwidOptions(myOptions);
      }
    }).catch((error) => {
      console.log(error);
    });
  }, []);

  useEffect(() => {
    const facilityID = parseInt(facility.value, 10);
    if (facilityID === 0 || Number.isNaN(facilityID)) {
      return;
    }

    setSelectedProgram(null);
    setSelectedEmailProgram(null);
    setSelectedServiceCodes([]);
    setServiceCodeOptions([]);
    setProgramCodeOptions([]);

    // Load and setup the Work List Chart data based on changing facility event.
    setIsLoadingChart(true);
    FacilityUsersProxy.getWorkListChart(facilityID)
      .then((chartData) => {
        const data: WorkListChartDataType = { facilityID, chartData };
        setIsLoadingChart(false);
        setWorkListChartData(data);
      })
      .catch((error) => {
        console.log(error);
      });

    getWorklist();
    getEmailedAssessments();

    const facilityId = parseInt(facility.value, 10);
    WorklistProxy.getServiceCodes(facilityId, undefined)
      .then((results) => {
        if (results.data) {
          const options = results.data.map((x) => ({
            label: x.unitCode,
            value: x.unitCode,
          }));
          setServiceCodeOptions(options);
        }
      })
      .catch((error) => {
        console.log(error);
      });
    DataListsProxy.getPrograms(facilityId, undefined)
      .then((results) => {
        const options: ProgramSelectOption[] = results.map((x) => ({
          label: x.programName,
          value: x.programID.toString(),
        }));
        setProgramCodeOptions(options);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [facility]);

  useEffect(() => {
    if (!workListLoading) {
      Utilities.setCellTitles();
    }
  }, [workListLoading]);

  // Client-side search/filter. This should ideally be a client-side search.
  const searchWorkList = (searchTerm: string): void => {
    if (searchTerm === '') {
      setFilteredList(workListData);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    const tempFilteredData: ProviderWorkListRow[] = workListData.filter((row) => {
      const accountNo = String(row.accountNumber);
      const medicalRecord = String(row.medicalRecord);
      const lowerPatientName = row.patientName.toLowerCase();
      const lowerAccountNo = accountNo.toLowerCase();
      const lowerMedicalRecord = medicalRecord.toLowerCase();
      return lowerPatientName.indexOf(lowerSearchTerm) !== -1
      || lowerAccountNo.indexOf(lowerSearchTerm) !== -1
      || lowerMedicalRecord.indexOf(lowerSearchTerm) !== -1;
    });
    setFilteredList(tempFilteredData);
  };

  const searchEmailedAssessments = (searchTerm: string): void => {
    if (searchTerm === '') {
      setFilteredEmailedAssessmentsData(emailedAssessmentsData);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    const tempFilteredData: EmailedAssessmentRow[] = emailedAssessmentsData.filter((row) => {
      const accountNo = String(row.accountNumber);
      const medicalRecord = String(row.medicalRecord);
      const lowerPatientName = row.patientName.toLowerCase();
      const lowerAccountNo = accountNo.toLowerCase();
      const lowerMedicalRecord = medicalRecord.toLowerCase();
      const lowerAssessmentSentTo = row.assessmentSentTo.toLowerCase();
      return lowerPatientName.indexOf(lowerSearchTerm) !== -1
      || lowerAccountNo.indexOf(lowerSearchTerm) !== -1
      || lowerMedicalRecord.indexOf(lowerSearchTerm) !== -1
      || lowerAssessmentSentTo.indexOf(lowerSearchTerm) !== -1;
    });
    setFilteredEmailedAssessmentsData(tempFilteredData);
  };

  const exportWorklistToExcel = (): void => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'Patient Name', key: 'name', width: 32 },
      { header: 'Account Number', key: 'accountNumber', width: 15 },
      { header: 'Patient ID', key: 'patientID', width: 15 },
      { header: 'Medical Record Number', key: 'medicalRecordNumber', width: 15 },
      { header: 'Admission Date', key: 'admissionDate', width: 20 },
      { header: 'Program', key: 'program', width: 32 },
      { header: 'Unit', key: 'unit', width: 15 },
      { header: 'Provider', key: 'provider', width: 15 },
      { header: 'Patient Status', key: 'patientStatus', width: 15 },
    ];

    for (let i = 0; i < filteredList.length; i += 1) {
      worksheet.addRow({
        name: filteredList[i].patientName,
        accountNumber: filteredList[i].accountNumber,
        patientID: filteredList[i].patientID,
        medicalRecordNumber: filteredList[i].medicalRecord,
        admissionDate: new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(filteredList[i].admissionDate)
          ? new MHODateTime(filteredList[i].admissionDate).getFormattedCalendarDate()
          : filteredList[i].admissionDate,
        program: filteredList[i].program,
        unit: filteredList[i].unit,
        provider: filteredList[i].provider,
        patientStatus: filteredList[i].patientStatus,
      });
    }

    const row = worksheet.getRow(1);
    row.font = { bold: true };

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => saveAs(new Blob([buffer]), `Facility_Worklist_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`))
      .catch((err) => console.log('Error writing excel export', err));
  };

  const exportEmailedAssessmentsToExcel = (): void => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'Patient Name', key: 'name', width: 32 },
      { header: 'Account Number', key: 'accountNumber', width: 15 },
      { header: 'Medical Record Number', key: 'medicalRecordNumber', width: 22 },
      { header: 'Admission Date', key: 'admissionDate', width: 20 },
      { header: 'Discharge Date', key: 'dischargeDate', width: 20 },
      { header: 'Program', key: 'program', width: 32 },
      { header: 'User', key: 'user', width: 32 },
      { header: 'Assessment Sent To', key: 'assessmentSentTo', width: 32 },
      { header: 'Assessment', key: 'assessment', width: 40 },
      { header: 'Assessment Time Period', key: 'assessmentTimePeriod', width: 25 },
      { header: 'Assessment Link Status', key: 'assessmentLinkStatus', width: 32 },
    ];

    for (let i = 0; i < filteredEmailedAssessmentsData.length; i += 1) {
      worksheet.addRow({
        name: filteredEmailedAssessmentsData[i].patientName,
        accountNumber: filteredEmailedAssessmentsData[i].accountNumber,
        patientID: filteredEmailedAssessmentsData[i].patientID,
        medicalRecordNumber: filteredEmailedAssessmentsData[i].medicalRecord,
        admissionDate: new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(filteredEmailedAssessmentsData[i].admissionDate)
          ? new MHODateTime(filteredEmailedAssessmentsData[i].admissionDate).getFormattedCalendarDate()
          : filteredEmailedAssessmentsData[i].admissionDate,
        dischargeDate: new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(filteredEmailedAssessmentsData[i].dischargeDate)
          ? new MHODateTime(filteredEmailedAssessmentsData[i].dischargeDate).getFormattedCalendarDate()
          : filteredEmailedAssessmentsData[i].dischargeDate,
        program: filteredEmailedAssessmentsData[i].program,
        user: filteredEmailedAssessmentsData[i].user,
        assessmentSentTo: filteredEmailedAssessmentsData[i].assessmentSentTo,
        assessment: filteredEmailedAssessmentsData[i].assessment,
        assessmentTimePeriod: filteredEmailedAssessmentsData[i].assessmentTimePeriod,
        assessmentLinkStatus: filteredEmailedAssessmentsData[i].assessmentLinkStatus,
      });
    }

    const row = worksheet.getRow(1);
    row.font = { bold: true };

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => saveAs(new Blob([buffer]), `Emailed_Assessments_List_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`))
      .catch((err) => console.log('Error writing excel export', err));
  };

  useEffect(() => {
    searchWorkList(worklistSearchText);
  }, [workListData]);

  useEffect(() => {
    searchEmailedAssessments(emailedAssessmentsSearchText);
  }, [emailedAssessmentsData]);

  // CVS Export button
  const CSVDownloadLink = (onExportClick: () => void): React.JSX.Element => (
    <Button variant={ButtonVariant.PRIMARY} onClick={onExportClick} extraClasses="worklist-button no-print">
      <InlineText text="Export to Excel" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
    </Button>
  );

  function handleDatepickerSelect(selectedDate: Date | null, dateType: string): void {
    if (dateType === 'startDate') {
      setStartDate(selectedDate);
      return;
    }
    setEndDate(selectedDate);
  }

  function invalidDate(myDate: any, isStartDate: boolean): string | undefined {
    const currDate = new Date();

    if (!myDate) {
      return '';
    }

    if (isStartDate && startDate && endDate && startDate > endDate) {
      return 'Start date cannot be greater than end date.';
    }

    if (endDate && !startDate) {
      return 'Start date required.';
    }

    if (myDate > currDate) {
      return 'Date cannot be in the future';
    }

    return '';
  }

  // Setup click event to view the patient
  const goToPatientDetails = (row: ProviderWorkListRow | EmailedAssessmentRow, e: React.MouseEvent<Element, MouseEvent>): void => {
    e.preventDefault();
    history.push(`${AllRoutes.PATIENT_ACCOUNTS}/${row.patientID}`);
  };

  const handleWorklistSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setWorklistSearchText(e.target.value);
    searchWorkList(e.target.value);
  };

  const handleEmailedAssessmentsSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmailedAssessmentsSearchText(e.target.value);
    searchEmailedAssessments(e.target.value);
  };

  /**
   * Handle "View By" filter change between 'Program' and 'Service Code'.
   *
   * @param e click event
   */
  const chartFilterChanged = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setShowProgramChart(e.target.value === 'program');
  };

  if (user?.accessType === AccessType.CORE_MEASURES) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText={props.testText} title={props.title}>
        Redirecting...
      </PageLayout>
    );
  }

  const worklistSection = (): React.JSX.Element => (
    <>
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="card-primary chart-container">
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="card-primary">
          <div className="control-bar-top">
            <InlineText text="View By:" fontColor={FontColors.PRIMARY} fontSize={FontSizes.REGULAR} bold />
            <input type="radio" id="program-work-list-rb" name="work-list-type-rb" value="program" onChange={chartFilterChanged} checked={showProgramChart} />
            <label htmlFor="program-work-list-rb">Program</label>
            <input type="radio" id="service-work-list-rb" name="work-list-type-rb" value="service-code" onChange={chartFilterChanged} checked={!showProgramChart} />
            <label htmlFor="service-work-list-rb">Service Code</label>
          </div>
        </FlexContainer>
        <WorkListChart workListChartData={workListChartData} isLoadingChart={isLoadingChart} isProgram={showProgramChart} />
      </FlexContainer>
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.SPACE_BETWEEN} extraClasses="work-list-section">
        <SecondaryHeader text="Work List" fontColor={FontColors.PRIMARY} extraClasses="work-list-section__header" />
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section__inputs">
          {programCodeOptions.length === 0 && (
            <LabeledSingleSelect
              // placeholder="Select a program"
              styles={MultiStandardDropdownStyles}
              options={programCodeOptions}
              defaultValue={selectedProgram ?? { label: 'Loading...', value: '' }}
              value={selectedProgram ?? { label: 'Loading...', value: '' }}
              onSelection={(option) => setSelectedProgram(option)}
              label="Select a program"
            />
          )}
          {programCodeOptions.length > 0 && (
            <LabeledSingleSelect
              // placeholder="Select a program"
              styles={MultiStandardDropdownStyles}
              options={programCodeOptions}
              defaultValue={null}
              value={selectedProgram ?? undefined}
              onSelection={(option) => setSelectedProgram(option)}
              label="Select a program"
              isClearable
            />
          )}
          {serviceCodeOptions.length === 0 && (
            <LabeledMultiSelect
              placeholder="Select or Search for Service"
              styles={MultiStandardDropdownStyles}
              options={serviceCodeOptions}
              defaultValue={selectedServiceCodes ?? [{ label: 'Loading...', value: '' }]}
              value={selectedServiceCodes ?? [{ label: 'Loading...', value: '' }]}
              onSelection={setSelectedServiceCodes}
              label="Select services"
            />
          )}
          {serviceCodeOptions.length > 0 && (
            <LabeledMultiSelect
              placeholder="Select or Search for Service"
              styles={MultiStandardDropdownStyles}
              options={serviceCodeOptions}
              defaultValue={null}
              value={selectedServiceCodes}
              onSelection={setSelectedServiceCodes}
              label="Select services"
            />
          )}
          {statusOptions.length === 0 && (
            <LabeledSingleSelect
              // placeholder="Select a program"
              styles={MultiStandardDropdownStyles}
              options={statusOptions}
              defaultValue={selectedStatus ?? { label: 'Loading...', value: '' }}
              value={selectedStatus ?? { label: 'Loading...', value: '' }}
              onSelection={(option) => setSelectedStatus(option)}
              label="Select a status"
            />
          )}
          {statusOptions.length > 0 && (
            <LabeledSingleSelect
              // placeholder="Select a program"
              styles={MultiStandardDropdownStyles}
              options={statusOptions}
              defaultValue={selectedStatus ?? null}
              value={selectedStatus ?? undefined}
              onSelection={(option) => setSelectedStatus(option)}
              label="Select a status"
              isClearable
            />
          )}
        </FlexContainer>
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section__inputs">
          <label className="labeled-single-select">
            <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="labeled-single-select__header">
              <Paragraph text="Search work list" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
            </FlexContainer>
            <SearchBar placeholderText="Type to search work list" searchText={worklistSearchText} inputName="PatientWorklist" handleSearchChange={handleWorklistSearchChange} />
          </label>

          <LabeledDatePickerInput
            label="Admission Start Date"
            name="startDate"
            onChange={(selectedDate) => handleDatepickerSelect(selectedDate, 'startDate')}
            error={invalidDate(startDate, true)}
            testid="from-date-input"
            initialValue={startDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
            placeholder="MMDDYYYY"
          />
          <LabeledDatePickerInput
            label="Admission End Date"
            name="endDate"
            onChange={(selectedDate) => handleDatepickerSelect(selectedDate, 'endDate')}
            error={invalidDate(endDate, false)}
            testid="end-date-input"
            initialValue={endDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
            placeholder="MMDDYYYY"
          />
        </FlexContainer>
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.END} extraClasses="work-list-section__buttons">
          {CSVDownloadLink(exportWorklistToExcel)}
          <Button variant={ButtonVariant.PRIMARY} onClick={getWorklist} extraClasses="worklist-button">
            <InlineText text="Apply" fontColor={FontColors.WHITE} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </FlexContainer>
      {workListLoading ? (
        <div className="loading-icon-overlay-container dashboard-loading-icon">
          <FontAwesomeIcon icon={faSpinner} className="fa-spin loading-icon-overlay" />
        </div>
      ) : <ProviderWorkList isLoading={workListLoading} data={filteredList} onRowClicked={goToPatientDetails} onChangePage={Utilities.setCellTitles} />}

    </>
  );

  const emailedAssessmentsSection = (): React.JSX.Element => (
    <>
      {' '}
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.SPACE_BETWEEN} extraClasses="work-list-section">
        <SecondaryHeader text="Emailed Assessments" fontColor={FontColors.PRIMARY} extraClasses="work-list-section__header" />
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section">
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section__inputs">
            {programCodeOptions.length === 0 && (
              <LabeledSingleSelect
              // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={programCodeOptions}
                defaultValue={selectedProgram ?? { label: 'Loading...', value: '' }}
                value={selectedEmailProgram ?? { label: 'Loading...', value: '' }}
                onSelection={(option) => setSelectedEmailProgram(option)}
                label="Select a program"
              />
            )}
            {programCodeOptions.length > 0 && (
              <LabeledSingleSelect
              // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={programCodeOptions}
                defaultValue={null}
                value={selectedEmailProgram ?? undefined}
                onSelection={(option) => setSelectedEmailProgram(option)}
                label="Select a program"
                isClearable
              />
            )}
            {awidOptions.length === 0 && (
              <LabeledSingleSelect
              // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={awidOptions}
                defaultValue={selectedAwid ?? { label: 'Loading...', value: '' }}
                value={selectedAwid ?? { label: 'Loading...', value: '' }}
                onSelection={(option) => setSelectedAwid(option)}
                label="Select assessment time period"
              />
            )}
            {awidOptions.length > 0 && (
              <LabeledSingleSelect
              // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={awidOptions}
                defaultValue={selectedAwid ?? null}
                value={selectedAwid ?? undefined}
                onSelection={(option) => setSelectedAwid(option)}
                label="Select assessment time period"
                isClearable
              />
            )}
          </FlexContainer>
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="work-list-section__inputs">
            <label className="labeled-single-select">
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="labeled-single-select__header">
                <Paragraph text="Search emailed assessments" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
              </FlexContainer>
              <SearchBar placeholderText="Type to search emailed assessments" searchText={emailedAssessmentsSearchText} inputName="PatientWorklist" handleSearchChange={handleEmailedAssessmentsSearchChange} />
            </label>

            {linkStatusOptions.length === 0 && (
              <LabeledSingleSelect
              // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={linkStatusOptions}
                defaultValue={selectedLinkStatus ?? { label: 'Loading...', value: '' }}
                value={selectedLinkStatus ?? { label: 'Loading...', value: '' }}
                onSelection={(option) => setSelectedLinkStatus(option)}
                label="Select assessment link status"
              />
            )}
            {linkStatusOptions.length > 0 && (
              <LabeledSingleSelect
              // placeholder="Select a program"
                styles={MultiStandardDropdownStyles}
                options={linkStatusOptions}
                defaultValue={selectedLinkStatus ?? null}
                value={selectedLinkStatus ?? undefined}
                onSelection={(option) => setSelectedLinkStatus(option)}
                label="Select assessment link status"
                isClearable
              />
            )}
          </FlexContainer>

        </FlexContainer>
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.END} extraClasses="work-list-section__buttons">
          {CSVDownloadLink(exportEmailedAssessmentsToExcel)}
          <Button variant={ButtonVariant.PRIMARY} onClick={getEmailedAssessments} extraClasses="worklist-button">
            <InlineText text="Apply" fontColor={FontColors.WHITE} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </FlexContainer>
      {emailedAssessmentsLoading ? (
        <div className="loading-icon-overlay-container dashboard-loading-icon">
          <FontAwesomeIcon icon={faSpinner} className="fa-spin loading-icon-overlay" />
        </div>
      ) : (
        <EmailedAssessmentsTable
          isLoading={emailedAssessmentsLoading}
          data={filteredEmailedAssessmentsData}
          onRowClicked={goToPatientDetails}
          onChangePage={Utilities.setCellTitles}
        />
      )}
    </>
  );

  return (
    <PageLayout layout={PageLayoutVariant.PADDED} testText={props.testText} loadingText={loading ? 'Loading...' : undefined} pageTitle={props.title}>
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="facility-dashboard-content">
        <Tabs>
          <TabList>
            <Tab>
              <InlineText text="Work List" fontColor={FontColors.DARK} fontSize={FontSizes.LARGE} />
            </Tab>
            <Tab>
              <InlineText text="Emailed Assessments" fontColor={FontColors.DARK} fontSize={FontSizes.LARGE} />
            </Tab>
          </TabList>
          <TabPanel>{worklistSection()}</TabPanel>
          <TabPanel>{emailedAssessmentsSection()}</TabPanel>
        </Tabs>

      </FlexContainer>

    </PageLayout>
  );
};
